import type React from 'react';
import { cn } from '@/lib/utils';

interface DraftMockupProps {
  readonly className?: string;
}

export function DraftMockup({ className }: DraftMockupProps): React.ReactElement {
  return (
    <div className={cn('relative group', className)}>
      <div className="relative z-10 p-2 bg-charcoal shadow-2xl transition-transform duration-500 group-hover:-translate-y-2">
        <div className="bg-cream-paper p-8 md:p-10 border border-charcoal/10 aspect-[3/4] flex flex-col justify-between">
          <div>
            {/* Eyebrow / dateline */}
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-8 bg-bottle-green" />
              <span className="smcp text-[11px] text-muted-warm font-medium">
                Draft &middot; Tuesday, May&nbsp;6
              </span>
            </div>

            {/* Dek (deck / standfirst) */}
            <p className="font-serif italic text-sm text-muted-warm mb-3 leading-snug">
              On building authority in a market that punishes shallow takes.
            </p>

            {/* Headline */}
            <h3 className="font-display text-3xl md:text-[2rem] text-charcoal mt-2 mb-5 leading-[1.1] tracking-tight">
              The Future of Content&nbsp;Strategy
            </h3>

            {/* Lede paragraph */}
            <p className="font-serif text-[13px] text-charcoal/80 leading-relaxed mb-3">
              In a market saturated with AI-generated fluff, the only durable
              advantage is research depth. Anyone can produce a 1,200-word post
              in twenty minutes&nbsp;<sup className="text-bottle-green font-semibold">[1]</sup>; few can
              produce one a senior buyer would forward to their team.
            </p>

            {/* Continuation paragraph (truncated by line-clamp visual) */}
            <p className="font-serif text-[13px] text-charcoal/70 leading-relaxed line-clamp-3">
              The difference is not in the prose itself but in the citation
              density, the editorial judgment that knows when an anecdote is
              doing real work, and the discipline to cut everything that&hellip;
            </p>
          </div>

          {/* Footer: version stamp + collaborator dots */}
          <div className="flex items-center justify-between pt-6 border-t border-charcoal/10">
            <span className="smcp text-[11px] text-muted-warm font-medium">
              Draft v1.4 &middot; 1,847 words
            </span>
            <div className="flex -space-x-2" aria-hidden="true">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="w-6 h-6 rounded-full border-2 border-cream-paper bg-aged-brass"
                  aria-hidden="true"
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Decorative offset frame */}
      <div className="absolute top-8 left-8 w-full h-full border-2 border-bottle-green/30 -z-10 transition-transform duration-500 group-hover:translate-x-2 group-hover:translate-y-2" />
    </div>
  );
}
